'use client';

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  normalizeDocumentationUrls,
  toasterApi,
  type ApplicationDocumentationController,
  type DocumentationFileCategory,
} from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../clients/testerClient';
import { applicationDocumentationApi } from '../../clients/tester-client/application-documentation-api';
import {
  groupApplicationDocumentationFiles,
  toUploadFileType,
} from '../../components/settings/target-settings/businessContext.transforms';
import { Logger } from '../../electron/logger';
import { useProjectStore } from '../../store/projectStore';
import { APPLICATION_FILES_QUERY_KEY } from './useHasApiDocumentation';

export const APPLICATION_DOCUMENTATION_URLS_QUERY_KEY =
  'application-documentation-urls';

const EMPTY_DOCUMENTATION_URLS: string[] = [];
const logger = new Logger('useApplicationDocumentationController');

export function useApplicationDocumentationController({
  applicationId,
}: {
  applicationId: string | undefined;
}): ApplicationDocumentationController {
  const queryClient = useQueryClient();
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const updateSelectedTarget = useProjectStore(
    (state) => state.updateSelectedTarget,
  );
  const isEnabled = Boolean(applicationId);

  const filesQuery = useQuery({
    queryKey: [APPLICATION_FILES_QUERY_KEY, applicationId],
    queryFn: () => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      return terraProxyApiClient.getApplicationFiles(applicationId);
    },
    enabled: isEnabled,
    refetchOnWindowFocus: false,
  });

  const documentationUrlsQuery = useQuery({
    queryKey: [APPLICATION_DOCUMENTATION_URLS_QUERY_KEY, applicationId],
    queryFn: async () => {
      if (!applicationId) {
        throw new Error('Application ID is required');
      }

      const application =
        await terraProxyApiClient.getApplicationById(applicationId);

      return normalizeDocumentationUrls(application.documentationUrls ?? []);
    },
    enabled: isEnabled,
    refetchOnWindowFocus: false,
  });

  const invalidateFiles = useCallback(
    (targetApplicationId: string) =>
      queryClient.invalidateQueries({
        queryKey: [APPLICATION_FILES_QUERY_KEY, targetApplicationId],
      }),
    [queryClient],
  );

  const uploadFilesMutation = useMutation({
    mutationFn: async ({
      applicationId: targetApplicationId,
      category,
      files,
    }: {
      applicationId: string;
      category: DocumentationFileCategory;
      files: File[];
    }) => {
      if (files.length === 0) {
        return;
      }

      await terraProxyApiClient.uploadApplicationFiles({
        applicationId: targetApplicationId,
        files,
        fileType: toUploadFileType(category),
      });
    },
    onSuccess: (_, variables) => invalidateFiles(variables.applicationId),
  });

  const deleteFileMutation = useMutation({
    mutationFn: async (variables: {
      applicationId: string;
      fileId: string;
    }) => {
      await applicationDocumentationApi.deleteApplicationFile(variables);
    },
    onSuccess: (_, variables) => invalidateFiles(variables.applicationId),
  });

  const downloadFileMutation = useMutation({
    mutationFn: async (variables: {
      applicationId: string;
      fileId: string;
      fileName: string;
    }) => {
      try {
        const { downloadUrl } =
          await applicationDocumentationApi.getApplicationFileDownloadUrl({
            applicationId: variables.applicationId,
            fileId: variables.fileId,
          });

        const result = await window.electronAPI.net.downloadFile(
          downloadUrl,
          variables.fileName,
        );

        if (result.success || result.error === 'Cancelled') {
          return;
        }

        logger.warn('Failed to download documentation file', {
          applicationId: variables.applicationId,
          fileId: variables.fileId,
          error: result.error,
        });
      } catch (error: unknown) {
        logger.warn('Failed to download documentation file', {
          applicationId: variables.applicationId,
          fileId: variables.fileId,
          error: error instanceof Error ? error.message : String(error),
        });
      }

      toasterApi.error({
        title: t('errors.failedToDownloadFile'),
        description: t('errors.pleaseTryAgain'),
      });
    },
  });

  const saveDocumentationUrlsMutation = useMutation({
    mutationFn: async ({
      applicationId: targetApplicationId,
      documentationUrls,
    }: {
      applicationId: string;
      documentationUrls: string[];
    }) => {
      const normalizedUrls = normalizeDocumentationUrls(documentationUrls);

      await applicationDocumentationApi.updateDocumentationUrls({
        applicationId: targetApplicationId,
        documentationUrls: normalizedUrls,
      });

      return normalizedUrls;
    },
    onSuccess: (normalizedUrls, variables) => {
      queryClient.setQueryData(
        [APPLICATION_DOCUMENTATION_URLS_QUERY_KEY, variables.applicationId],
        normalizedUrls,
      );
      const { selectedTarget } = useProjectStore.getState();

      if (selectedTarget?.id === variables.applicationId) {
        updateSelectedTarget({
          ...selectedTarget,
          documentationUrls: normalizedUrls,
        });
      }
    },
  });

  const groupedFiles = useMemo(
    () => groupApplicationDocumentationFiles(filesQuery.data?.files ?? []),
    [filesQuery.data],
  );
  const { mutateAsync: uploadFiles } = uploadFilesMutation;
  const { mutateAsync: deleteFile } = deleteFileMutation;
  const { mutateAsync: downloadFile } = downloadFileMutation;
  const { mutateAsync: persistDocumentationUrls } =
    saveDocumentationUrlsMutation;
  const saveDocumentationUrls = useCallback(
    async (variables: {
      applicationId: string;
      documentationUrls: string[];
    }) => {
      await persistDocumentationUrls(variables);
    },
    [persistDocumentationUrls],
  );

  return {
    ...groupedFiles,
    documentationUrls: documentationUrlsQuery.data ?? EMPTY_DOCUMENTATION_URLS,
    uploadFiles,
    deleteFile,
    downloadFile,
    saveDocumentationUrls,
    isLoadingDocumentation:
      filesQuery.isLoading || documentationUrlsQuery.isLoading,
    isDeletingFile: deleteFileMutation.isPending,
    isSavingDocumentationUrls: saveDocumentationUrlsMutation.isPending,
  };
}
